import React, { useState, useEffect, useCallback } from 'react';
import { Filter, X, SlidersHorizontal, ArrowDownAZ, Star, CheckCircle, Heart, PlusCircle, CalendarClock } from 'lucide-react';
import { GameSearchFilters } from '../types';
import { getAvailableFilters, loadAvailableFilters } from '../lib/gameService';
import RangeSlider from './RangeSlider';
import { FilterCategory, FilterValues } from '../types/filters';
import useDebounce from '../hooks/useDebounce';
import { Game } from '../types/game';

// Definir os valores padrão de filtros (usados apenas enquanto carregamos do servidor)
const DEFAULT_GAME_CATEGORIES = [
  'Estratégia', 'Familiar', 'Party Game', 'Aventura', 'Cooperativo'
];

const DEFAULT_GAME_MECHANICS = [
  'Dice Rolling', 'Worker Placement', 'Deck Building', 'Hand Management'
];

const DEFAULT_GAME_THEMES = [
  'Fantasia', 'Medieval', 'Sci-Fi', 'Horror', 'Histórico'
];

// Definir interface GameFilterState aqui já que a importação está dando erro
interface GameFilterState {
  categories: string[];
  mechanics: string[];
  themes: string[];
  minPlayers?: number;
  maxPlayers?: number;
  minPlayTime?: number;
  maxPlayTime?: number;
  minAge?: number;
}

export type SortOption = 'name' | 'rating_desc' | 'popularity_desc' | 'year_desc' | 'complexity_asc' | 'complexity_desc';

export interface GameFilterProps {
  filters: GameSearchFilters;
  showFilters: boolean;
  sortBy: SortOption;
  setShowFilters: (show: boolean) => void;
  onFilterChange: (filters: GameSearchFilters) => void;
  onSortChange: (sortOption: SortOption) => void;
  onClearFilters: () => void;
  disabled?: boolean;
  hidePlayerFilter?: boolean;
  hideTimeFilter?: boolean;
  hideComplexityFilter?: boolean;
  hideSortSelector?: boolean;
  enableInternalFilterPagination?: boolean;
  internalFilterInitialItemLimit?: number;
}

const GameFilters: React.FC<GameFilterProps> = ({
  filters,
  showFilters,
  sortBy,
  setShowFilters,
  onFilterChange,
  onSortChange,
  onClearFilters,
  disabled = false,
  hidePlayerFilter = false,
  hideTimeFilter = false,
  hideComplexityFilter = false,
  hideSortSelector = false,
  enableInternalFilterPagination = true,
  internalFilterInitialItemLimit = 5,
  // Adicionar hideMinAgeFilter se necessário no futuro
  // hideMinAgeFilter = false 
}) => {
  const [activeFilterTab, setActiveFilterTab] = useState<'categories' | 'mechanics' | 'themes'>('categories');
  const [availableFilters, setAvailableFilters] = useState({
    categories: DEFAULT_GAME_CATEGORIES,
    mechanics: DEFAULT_GAME_MECHANICS,
    themes: DEFAULT_GAME_THEMES,
    lastUpdated: null as Date | null
  });
  const [isLoading, setIsLoading] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState<FilterValues>({});
  const [loadingFilters, setLoadingFilters] = useState(true);
  
  // Estados para paginação dos filtros de tags
  const [showAllCategories, setShowAllCategories] = useState(false);
  const [showAllMechanics, setShowAllMechanics] = useState(false);
  const [showAllThemes, setShowAllThemes] = useState(false);
  
  // Configuração dos valores de jogadores (apenas máximo)
  const [playerMax, setPlayerMax] = useState<number | undefined>(filters.maxPlayers);
  
  // Configuração dos valores de faixa de tempo de jogo
  const [playtimeMax, setPlaytimeMax] = useState<number | undefined>(filters.maxPlayTime);
  
  // Estado para minAge
  const [minAge, setMinAge] = useState<number | undefined>(filters.minAge);
  
  // Define o valor de ordenação atual
  const [sortOption, setSortOption] = useState<SortOption>(sortBy);
  
  // Aplica debounce nas mudanças dos sliders para evitar muitas atualizações
  const debouncedPlayerMax = useDebounce(playerMax, 300);
  const debouncedPlaytimeMax = useDebounce(playtimeMax, 300);
  
  // Carregar os filtros disponíveis do servidor
  useEffect(() => {
    const loadFilters = async () => {
      try {
        setIsLoading(true);
        
        // Primeiro, tentar obter do cache interno
        const cachedFilters = getAvailableFilters();
        if (cachedFilters.categories.length > 0) {
          setAvailableFilters({
            categories: cachedFilters.categories,
            mechanics: cachedFilters.mechanics,
            themes: cachedFilters.themes,
            lastUpdated: cachedFilters.lastUpdated || null
          });
          setIsLoading(false);
          return;
        }
        
        // Se não tiver no cache, carregar do servidor
        const freshFilters = await loadAvailableFilters();
        setAvailableFilters({
          categories: freshFilters.categories,
          mechanics: freshFilters.mechanics,
          themes: freshFilters.themes,
          lastUpdated: freshFilters.lastUpdated || null
        });
      } catch (error) {
        console.error('Erro ao carregar filtros:', error);
        // Em caso de erro, mantemos os valores padrão
      } finally {
        setIsLoading(false);
      }
    };
    
    loadFilters();
  }, []);

  const handleCategoryToggle = (category: string) => {
    const currentCategories = filters.categories || [];
    const newCategories = currentCategories.includes(category)
      ? currentCategories.filter(c => c !== category)
      : [...currentCategories, category];
    
    onFilterChange({
      ...filters,
      categories: newCategories
    });
  };

  const handleMechanicToggle = (mechanic: string) => {
    const currentMechanics = filters.mechanics || [];
    const newMechanics = currentMechanics.includes(mechanic)
      ? currentMechanics.filter(m => m !== mechanic)
      : [...currentMechanics, mechanic];
    
    console.log('Aplicando filtro de mecânicas:', newMechanics);
    
    onFilterChange({
      ...filters,
      mechanics: newMechanics
    });
  };

  const handleThemeToggle = (theme: string) => {
    const currentThemes = filters.themes || [];
    const newThemes = currentThemes.includes(theme)
      ? currentThemes.filter(t => t !== theme)
      : [...currentThemes, theme];
    
    console.log('Aplicando filtro de temas:', newThemes);
    
    onFilterChange({
      ...filters,
      themes: newThemes
    });
  };

  // Handler para atualizar o máximo de jogadores
  const handlePlayerMaxChange = (max: number | undefined) => {
    const newMax = max === 99 ? undefined : max; // Se for 99, considera como undefined (sem filtro)
    setPlayerMax(newMax);
    onFilterChange({
      ...filters,
      minPlayers: undefined, // Sempre indefinido
      maxPlayers: newMax
    });
  };

  // Handler para atualizar o tempo de jogo (apenas máximo)
  const handlePlaytimeMaxChange = (max: number | undefined) => {
    const newMax = max === 900 ? undefined : max; // Se for 900, considera como undefined (sem filtro)
    setPlaytimeMax(newMax);
    onFilterChange({
      ...filters,
      minPlayTime: undefined, // Sempre indefinido
      maxPlayTime: newMax
    });
  };

  // Handler para atualizar minAge
  const handleMinAgeChange = (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const value = event.target.value ? parseInt(event.target.value, 10) : undefined;
    setMinAge(value);
    onFilterChange({
      ...filters,
      minAge: value,
    });
  };

  const countActiveFilters = () => {
    let count = 0;
    if (filters.categories && filters.categories.length > 0) count += filters.categories.length;
    if (filters.mechanics && filters.mechanics.length > 0) count += filters.mechanics.length;
    if (filters.themes && filters.themes.length > 0) count += filters.themes.length;
    if (filters.maxPlayers) count++;
    if (filters.maxPlayTime) count++;
    if (filters.minAge) count++;
    if (filters.minComplexity !== undefined && filters.minComplexity > 0) count++;
    if (filters.maxComplexity !== undefined && filters.maxComplexity < 5) count++;
    return count;
  };

  const filterToggleButton = (
    <button
      onClick={() => setShowFilters(!showFilters)}
      className={`flex items-center px-3 py-2 bg-white shadow rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200 ${
        disabled ? 'opacity-60 cursor-not-allowed' : 'hover:bg-gray-50 dark:hover:bg-gray-600'
      }`}
      title={disabled ? "Carregando jogos..." : (showFilters ? "Ocultar filtros" : "Mostrar filtros")}
      disabled={disabled}
    >
      <Filter className={`h-5 w-5 ${showFilters ? 'text-indigo-600 dark:text-indigo-400' : 'text-gray-400 dark:text-gray-500'}`} />
      <span className="ml-2 dark:text-gray-100">Filtros</span>
      {countActiveFilters() > 0 && (
        <span className="ml-2 px-2 py-0.5 bg-indigo-100 text-indigo-800 text-xs rounded-full dark:bg-indigo-500/40 dark:text-indigo-200">
          {countActiveFilters()}
        </span>
      )}
    </button>
  );

  const sortSelector = !hideSortSelector ? (
    <div className={`flex items-center bg-white shadow rounded-lg px-3 py-2 dark:bg-gray-700 dark:border-gray-600 ${
      disabled ? 'opacity-60 cursor-not-allowed' : ''
    }`}>
      <ArrowDownAZ className="h-5 w-5 text-gray-400 dark:text-gray-500 mr-2" />
      <select
        className="bg-transparent outline-none flex-1 text-gray-700 dark:text-gray-100 dark:bg-gray-700"
        value={sortBy}
        onChange={(e) => onSortChange(e.target.value as SortOption)}
        disabled={disabled}
      >
        <option value="name">Nome (A-Z)</option>
        <option value="rating_desc">Melhor Avaliados</option>
        <option value="popularity_desc">Mais Populares</option>
        <option value="year_desc">Mais Recentes</option>
        <option value="complexity_asc">Complexidade (Menor)</option>
        <option value="complexity_desc">Complexidade (Maior)</option>
      </select>
    </div>
  ) : null;

  // Função para calcular os valores mínimos e máximos para os sliders com base nos jogos disponíveis
  const calculateRanges = useCallback(() => {
    if (!availableFilters.lastUpdated || !availableFilters.categories.length || !availableFilters.mechanics.length || !availableFilters.themes.length) return;
    
    // Ajustado para apenas maxPlayers
    if (!filters.maxPlayers) {
      setPlayerMax(undefined);
    }
    
    // Ajustado para apenas maxPlayTime
    if (!filters.maxPlayTime) {
      setPlaytimeMax(undefined);
    }
  }, [availableFilters, filters]);
  
  // Carregar filtros disponíveis do banco de dados
  useEffect(() => {
    const loadFilters = async () => {
      try {
        setLoadingFilters(true);
        const availableFiltersData = await loadAvailableFilters();
        
        // Mesclar os filtros carregados com os valores padrão e manter o lastUpdated
        const mergedFiltersData = {
          categories: availableFiltersData.categories || DEFAULT_GAME_CATEGORIES,
          mechanics: availableFiltersData.mechanics || DEFAULT_GAME_MECHANICS,
          themes: availableFiltersData.themes || DEFAULT_GAME_THEMES,
          lastUpdated: availableFiltersData.lastUpdated || null
        };
        
        setAvailableFilters(mergedFiltersData);
        
        // Inicializar filtros selecionados com valores iniciais, apenas na primeira carga
        if (filters.categories && Object.keys(selectedFilters).length === 0) {
          const categoriesObject: FilterValues = {};
          
          filters.categories.forEach(category => {
            categoriesObject[category] = { selected: true };
          });
          
          setSelectedFilters(categoriesObject);
        }
      } catch (error) {
        console.error('Erro ao carregar filtros:', error);
      } finally {
        setLoadingFilters(false);
      }
    };
    
    loadFilters();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); 
  
  // Mova calculateRanges para um useEffect separado que roda apenas quando availableFilters muda
  useEffect(() => {
    calculateRanges();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableFilters]);
  
  // Atualiza os estados locais quando os filtros mudarem externamente
  useEffect(() => {
    // Ajustado para apenas maxPlayers
    const needsPlayerMaxUpdate = playerMax !== filters.maxPlayers;
    // Ajustado para apenas maxPlayTime
    const needsPlaytimeUpdate = playtimeMax !== filters.maxPlayTime;
    
    if (needsPlayerMaxUpdate) {
      setPlayerMax(filters.maxPlayers);
    }
    if (needsPlaytimeUpdate) {
      setPlaytimeMax(filters.maxPlayTime);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ filters.maxPlayers, filters.maxPlayTime ]);
  
  // Corrigir a função toggleFilter para lidar com FilterValues corretamente
  const toggleFilter = (categoryId: string, valueId: string | number) => {
    setSelectedFilters((prevFilters: FilterValues) => {
      const categoryFilters = prevFilters[categoryId] || {};
      const updatedCategoryFilters = { ...categoryFilters, [valueId]: !categoryFilters[valueId as string] };
      const allFalse = Object.values(updatedCategoryFilters).every(v => !v);
      
      if (allFalse) {
        const { [categoryId]: _, ...rest } = prevFilters;
        return rest;
      }
      
      return { ...prevFilters, [categoryId]: updatedCategoryFilters };
    });
  };
  
  // Função para limpar todos os filtros
  const handleClearFilters = () => {
    setSelectedFilters({});
    setPlayerMax(undefined); // Limpar apenas o máximo de jogadores
    setPlaytimeMax(undefined); // Limpar apenas o máximo
    setMinAge(undefined);
    setSortOption('name');
    onClearFilters(); // onClearFilters deve setar minPlayers: undefined, maxPlayers: undefined, minPlayTime: undefined, maxPlayTime: undefined
  };
  
  // Manipulador para alteração da ordenação
  const handleSortChange = (sortId: string) => {
    const newSort = ['name', 'rating_desc', 'popularity_desc', 'year_desc', 'complexity_asc', 'complexity_desc'].find(option => option === sortId);
    if (newSort) {
      setSortOption(newSort as SortOption);
    }
  };
  
  // Efeito para aplicar debounce nos sliders e chamar onFilterChange
  useEffect(() => {
      // Enviar minPlayers como undefined
      onFilterChange({ ...filters, minPlayers: undefined, maxPlayers: debouncedPlayerMax });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedPlayerMax]);

  useEffect(() => {
      // Enviar minPlayTime como undefined
      onFilterChange({ ...filters, minPlayTime: undefined, maxPlayTime: debouncedPlaytimeMax });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedPlaytimeMax]);

  // Se showFilters for false, não renderiza nada (ou apenas o botão toggle, que está no pai)
  if (!showFilters) {
    return null;
  }

  return (
    <div className={`space-y-4 ${disabled ? 'pointer-events-none' : ''}`}>
      <div className="flex flex-wrap gap-3 items-center">
        {filterToggleButton}
        {sortSelector}
        <button 
          onClick={onClearFilters} 
          className={`flex items-center text-xs px-3 py-2.5 bg-white shadow rounded-lg hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 dark:hover:bg-gray-600 ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
          disabled={disabled || countActiveFilters() === 0}
          title={disabled ? "Carregando..." : countActiveFilters() === 0 ? "Nenhum filtro ativo" : "Limpar todos os filtros"}
        >
          <X className="h-4 w-4 mr-1" /> Limpar Filtros
        </button>
      </div>

      {showFilters && (
        <div className="p-4 bg-white rounded-lg shadow-lg border border-gray-200 dark:bg-gray-800 dark:border-gray-700">
          {isLoading ? (
            <div className="text-center py-8">
              <SlidersHorizontal className="h-8 w-8 mx-auto text-gray-400 animate-pulse dark:text-gray-500" />
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">Carregando opções de filtro...</p>
            </div>
          ) : (
            <>
              {/* Abas de Filtro */}
              <div className="mb-4 border-b border-gray-200 dark:border-gray-600">
                <nav className="-mb-px flex space-x-2" aria-label="Tabs">
                  {['categories', 'mechanics', 'themes'].map((tab) => (
                    <button
                      key={tab}
                      onClick={() => setActiveFilterTab(tab as any)}
                      className={`${activeFilterTab === tab
                          ? 'border-indigo-500 text-indigo-600 dark:border-indigo-400 dark:text-indigo-300'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:border-gray-600'
                        } whitespace-nowrap py-3 px-3 border-b-2 font-medium text-sm`}
                    >
                      {tab.charAt(0).toUpperCase() + tab.slice(1)}
                    </button>
                  ))}
                </nav>
              </div>

              {/* Conteúdo das Abas */}
              {activeFilterTab === 'categories' && (
                <div className="p-3 bg-white dark:bg-gray-700 rounded-md shadow-sm mt-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-4">
                    {(enableInternalFilterPagination ? (showAllCategories ? availableFilters.categories : availableFilters.categories.slice(0, internalFilterInitialItemLimit)) : availableFilters.categories).map(category => (
                      <label key={category} className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={filters.categories?.includes(category) || false}
                          onChange={() => handleCategoryToggle(category)}
                          className="form-checkbox h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 dark:border-gray-500 dark:bg-gray-700 dark:focus:ring-offset-gray-800 dark:checked:bg-indigo-500 dark:checked:border-indigo-500"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-200">{category}</span>
                      </label>
                    ))}
                  </div>
                  {enableInternalFilterPagination && availableFilters.categories.length > internalFilterInitialItemLimit && (
                    <div className="md:col-span-2 lg:col-span-3 mt-2">
                      <button 
                        onClick={() => setShowAllCategories(!showAllCategories)}
                        className="text-xs text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300 font-medium"
                      >
                        {showAllCategories ? 'Ver menos categorias' : `Ver mais ${availableFilters.categories.length - internalFilterInitialItemLimit} categorias...`}
                      </button>
                    </div>
                  )}
                </div>
              )}
              {activeFilterTab === 'mechanics' && (
                <div className="p-3 bg-white dark:bg-gray-700 rounded-md shadow-sm mt-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-4">
                    {(enableInternalFilterPagination ? (showAllMechanics ? availableFilters.mechanics : availableFilters.mechanics.slice(0, internalFilterInitialItemLimit)) : availableFilters.mechanics).map(mechanic => (
                      <label key={mechanic} className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={filters.mechanics?.includes(mechanic) || false}
                          onChange={() => handleMechanicToggle(mechanic)}
                          className="form-checkbox h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 dark:border-gray-500 dark:bg-gray-700 dark:focus:ring-offset-gray-800 dark:checked:bg-indigo-500 dark:checked:border-indigo-500"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-200">{mechanic}</span>
                      </label>
                    ))}
                  </div>
                  {enableInternalFilterPagination && availableFilters.mechanics.length > internalFilterInitialItemLimit && (
                    <div className="md:col-span-2 lg:col-span-3 mt-2">
                      <button 
                        onClick={() => setShowAllMechanics(!showAllMechanics)}
                        className="text-xs text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300 font-medium"
                      >
                        {showAllMechanics ? 'Ver menos mecânicas' : `Ver mais ${availableFilters.mechanics.length - internalFilterInitialItemLimit} mecânicas...`}
                      </button>
                    </div>
                  )}
                </div>
              )}
              {activeFilterTab === 'themes' && (
                <div className="p-3 bg-white dark:bg-gray-700 rounded-md shadow-sm mt-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-4">
                    {(enableInternalFilterPagination ? (showAllThemes ? availableFilters.themes : availableFilters.themes.slice(0, internalFilterInitialItemLimit)) : availableFilters.themes).map(theme => (
                      <label key={theme} className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={filters.themes?.includes(theme) || false}
                          onChange={() => handleThemeToggle(theme)}
                          className="form-checkbox h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 dark:border-gray-500 dark:bg-gray-700 dark:focus:ring-offset-gray-800 dark:checked:bg-indigo-500 dark:checked:border-indigo-500"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-200">{theme}</span>
                      </label>
                    ))}
                  </div>
                  {enableInternalFilterPagination && availableFilters.themes.length > internalFilterInitialItemLimit && (
                    <div className="md:col-span-2 lg:col-span-3 mt-2">
                      <button 
                        onClick={() => setShowAllThemes(!showAllThemes)}
                        className="text-xs text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300 font-medium"
                      >
                        {showAllThemes ? 'Ver menos temas' : `Ver mais ${availableFilters.themes.length - internalFilterInitialItemLimit} temas...`}
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Filtro de Idade Mínima - MOVENDO PARA ANTES DOS RANGE FILTERS */}
              <div className="mb-4 p-3 mt-4 bg-white dark:bg-gray-700 rounded-md shadow-sm">
                <label htmlFor="minAge" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Idade Mínima Recomendada
                </label>
                <select
                  id="minAge"
                  name="minAge"
                  value={minAge === undefined ? '' : minAge}
                  onChange={handleMinAgeChange}
                  disabled={disabled}
                  className="w-full p-2 border border-gray-300 rounded-md text-sm dark:bg-gray-600 dark:text-gray-100 dark:border-gray-500 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="">Qualquer Idade</option>
                  <option value="3">3+</option>
                  <option value="4">4+</option>
                  <option value="5">5+</option>
                  <option value="6">6+</option>
                  <option value="7">7+</option>
                  <option value="8">8+</option>
                  <option value="9">9+</option>
                  <option value="10">10+</option>
                  <option value="12">12+</option>
                  <option value="13">13+</option>
                  <option value="14">14+</option>
                  <option value="16">16+</option>
                  <option value="17">17+</option>
                  <option value="18">18+</option>
                </select>
              </div>

              {/* Filtros de Range (Jogadores, Tempo) */}
              <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-600 grid grid-cols-1 md:grid-cols-2 gap-6">
                {!hidePlayerFilter && (
                  <div className="p-3 bg-white dark:bg-gray-700 rounded-md shadow-sm">
                    <div className="flex justify-between items-center mb-2">
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-100">Máx. de Jogadores</h4>
                      <span className="text-sm text-gray-500 dark:text-gray-300">
                        {filters.maxPlayers === undefined ? 'Até 99' : `Até ${filters.maxPlayers}`}
                      </span>
                    </div>
                    <input
                      type="range"
                      min="1"
                      max="99"
                      step="1"
                      value={playerMax ?? 99}
                      onChange={(e) => handlePlayerMaxChange(parseInt(e.target.value, 10))}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-600"
                      disabled={disabled}
                    />
                    <div className="text-center text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {playerMax === undefined ? '99 jogadores' : `${playerMax} jogadores`}
                    </div>
                  </div>
                )}
                {!hideTimeFilter && (
                  <div className="p-3 bg-white dark:bg-gray-700 rounded-md shadow-sm">
                    <div className="flex justify-between items-center mb-2">
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-100">Tempo Máx. de Jogo</h4>
                      <span className="text-sm text-gray-500 dark:text-gray-300">
                        {filters.maxPlayTime === undefined ? 'Até 900 min' : `Até ${filters.maxPlayTime} min`}
                      </span>
                    </div>
                    <input
                      type="range"
                      min="15"
                      max="900"
                      step="15"
                      value={playtimeMax ?? 900}
                      onChange={(e) => handlePlaytimeMaxChange(parseInt(e.target.value, 10))}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-600"
                      disabled={disabled}
                    />
                    <div className="text-center text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {playtimeMax === undefined ? '900 min' : `${playtimeMax} min`}
                    </div>
                  </div>
                )}
              </div>

              {/* Botão Limpar Filtros e Aplicar */}
              <div className="mt-6 flex flex-col sm:flex-row justify-between items-center gap-3 p-3 bg-white dark:bg-gray-700 rounded-md shadow-sm">
                <button
                  onClick={handleClearFilters}
                  disabled={disabled}
                  className={`w-full sm:w-auto px-4 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-500 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  Limpar Filtros Aplicados ({countActiveFilters()})
                </button>
                <button
                  onClick={() => setShowFilters(false)} // Simplesmente fecha o painel
                  disabled={disabled}
                  className={`w-full sm:w-auto px-4 py-2 text-sm rounded-md bg-indigo-600 text-white hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600 transition-colors ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  Aplicar e Fechar Filtros
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default GameFilters; 